import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
// Components
import verificationMessages from 'app/_authenticated/verification/messages';
import { Button } from 'components/Button/Button';
import { Card } from 'components/Card/Card';
import CurrencyIcon from 'components/CurrencyIcon/CurrencyIcon';
import { ProductTabs } from 'components/Tabs/ProductTabs';
// Entities
import { ProductCopy } from 'types/products';
// Store
// Misc
import {
  AssetCard,
  Heading,
  Markdown,
  StatsBox,
  Typography,
} from '@altify/components';
import {
  AdvancedTable,
  AdvancedTableCell,
} from 'components/AdvancedTable/AdvancedTable';
import { SortableHeader } from 'components/AdvancedTable/SortableHeader';
import { CustomTabs } from 'components/CustomTabs/CustomTabs';
import { DialogOrBottomSheet } from 'components/DialogOrBottomSheet/DialogOrBottomSheet';
import { InfoButton } from 'components/InfoButton/InfoButton';
import { ProductTabItem } from 'components/Tabs/ProductTabs';
import {
  Product,
  ProductWithHoldingsAndRates,
  useProductWithHoldingsAndRates,
} from 'hooks/products/useProducts';
import useAppLanguage from 'hooks/useAppLanguage';
import { useVerification } from 'hooks/useVerification';
import moment from 'moment';
import { useGetProductDetailsQuery } from 'store/slices/products-api';
import { useGetProfileQuery } from 'store/slices/profile-api';
import appHelpers from 'utils/app-urls';
import {
  privateCreditProductCards,
  privateCreditProductTabs,
} from 'utils/constants';
import { formatCurrency, getCurrencyTypeLabel } from 'utils/currencies';
import { replaceTextInObject } from 'utils/utils';
import productMessages from '../messages';
import ProductButtons from './ProductButtons';
import Modals from './ProductModals/ProductModals';
import { ActiveProductDtoTypeEnum } from 'services/generated/api';

const cdnURL = appHelpers.getCDNUrl();

export function PrivateCreditProductData({ code }: { code: string }) {
  const navigate = useNavigate();
  const { data: profileData } = useGetProfileQuery();
  const language = useAppLanguage();
  const { product } = useProductWithHoldingsAndRates(code);
  const { data: productDetails } = useGetProductDetailsQuery(code);
  const { isVerified, isVerificationPending, isVerificationInProgress } =
    useVerification();

  const productBanner = `${cdnURL}assets/banners/products/${code.toLocaleLowerCase()}.png`;
  const productMobileBanner = `${cdnURL}assets/banners/products/${code.toLocaleLowerCase()}-mobile.png`;
  const productChart = `${cdnURL}assets/products/general/${code.toLocaleLowerCase()}.png`;
  const productMobileChart = `${cdnURL}assets/products/general/${code.toLocaleLowerCase()}-chart.png`;
  const [sorting, setSorting] = useState<{
    direction: 'asc' | 'desc';
    key: string;
  }>({ direction: 'asc', key: 'displayOrder' });

  const [returnsModalOpen, setReturnsModalOpen] = useState(false);
  const [verifyModal, setVerifyModal] = useState(false);
  const [infoContent, setInfoContent] = useState('');
  const [infoTitle, setInfoTitle] = useState('');
  const [activeProductTab, setActiveProductTab] = useState<string>('overview');
  const productTabs = privateCreditProductTabs[language];
  const productCards = privateCreditProductCards[language];
  const activeTabIndex = productTabs.findIndex(
    (item: ProductTabItem) => item.value === activeProductTab,
  );

  const messages = productMessages[language].productData;
  const verificationPendingMessages = verificationMessages[language];

  const handleTabSelect = (tabValue: string) => {
    setActiveProductTab(tabValue);
  };

  /*This is the quick and dirty solution for ALFI*/
  const productCopy =
    profileData?.jurisdictionCode?.toUpperCase() === 'EU'
      ? replaceTextInObject(productDetails?.productCopy, /USDT/g, 'USDC')
      : productDetails?.productCopy;

  const holdingsItems = [
    {
      label: messages.balance,
      value: (
        <>
          <div className="flex flex-row justify-between">
            <Typography size="medium">
              {formatCurrency(product?.totalAmount, code)}
            </Typography>
          </div>
        </>
      ),
    },
    {
      label: messages.value,
      value: (
        <>
          <div className="flex flex-row justify-between">
            <Typography size="medium">
              {formatCurrency(product?.totalAmountInDisplay, code)}
            </Typography>
          </div>
        </>
      ),
    },
    {
      label: messages.yield,
      value: (
        <>
          <div className="flex flex-row justify-between">
            <Typography size="medium">{productCopy?.yield}</Typography>
          </div>
        </>
      )
    },
    {
      label: messages.maturity,
      value: (
        <>
          <div className="flex flex-row justify-between">
            <Typography size="medium">{productCopy?.maturity}</Typography>
          </div>
        </>
      )
    },
  ];

  const getProductCopySection = (tab: string) => {
    return productCopy ? (productCopy[tab as keyof ProductCopy] ?? '') : '';
  };

  const productTabCopy = getProductCopySection(activeProductTab);

  const holdingsRows = [
    {
      date: product?.holdingsLastChangedAt,
      amount: product?.totalAmount,
      value: product?.totalAmountInDisplay,
      maturity: productCopy?.maturity,
    },
  ];

  const columns = useMemo(
    () => {
      const dataColumns = [
        {
          accessor: 'date',
          alignHeader: 'left',
          Header: (
            <SortableHeader
              label={messages.date}
              value={messages.date}
              orderByColumn={sorting.key}
              order={sorting.direction}
              isSortable={false}
            />
          ),
          Cell: ({ row }: AdvancedTableCell<ProductWithHoldingsAndRates>) => (
            <div className="flex flex-row justify-between">
              <Typography size="small">
                {moment(row?.original?.holdingsLastChangedAt).format(
                  'DD MMMM YYYY · HH:mm',
                )}
              </Typography>
            </div>
          ),
        },
        {
          accessor: 'amount',
          alignHeader: 'right',
          Header: (
            <SortableHeader
              label={messages.balance}
              value={messages.balance}
              orderByColumn={sorting.key}
              order={sorting.direction}
              isSortable={false}
            />
          ),
          Cell: ({ row }: AdvancedTableCell<Product>) => (
            <div className="flex flex-row justify-end">
              <Typography size="small">
                {formatCurrency(product?.totalAmount, code)}
              </Typography>
            </div>
          ),
        },
        {
          accessor: 'value',
          alignHeader: 'left',
          Header: (
            <SortableHeader
              label={messages.value}
              value={messages.value}
              orderByColumn={sorting.key}
              order={sorting.direction}
              isSortable={false}
            />
          ),
          Cell: ({ row }: AdvancedTableCell<Product>) => (
            <div className="flex flex-col justify-between">
              <Typography size="small">
                {formatCurrency(product?.totalAmountInDisplay, code)}
              </Typography>
            </div>
          ),
        },
        {
          accessor: 'maturity',
          alignHeader: 'right',
          Header: (
            <SortableHeader
              label={messages.maturity}
              value={messages.maturity}
              orderByColumn={sorting.key}
              order={sorting.direction}
              isSortable={false}
            />
          ),
          Cell: ({ row, value }: AdvancedTableCell<Product>) => {
            return (
              <div className="flex flex-col flex-wrap items-end">
                <Typography size="small">
                  {productCopy?.maturity ?? '2 years'}
                </Typography>
              </div>
            );
          },
        },
        {
          accessor: 'action',
          alignHeader: 'right',
          Header: <></>,
          Cell: ({ row }: AdvancedTableCell<Product>) => {
            return (
              <div className="flex flex-row justify-end">
                <button
                  type="button"
                  className="justify-end h-7 bg-neutral-500 hover:bg-primary rounded-full px-4 py-0.5"
                  onClick={() => window.open(productCopy?.redeemLink ?? '')}>
                  <Typography size="small" className="h-5 m-0">
                    {messages.redeemButton}
                  </Typography>
                </button>
              </div>
            );
          },
        },
      ];

      return dataColumns;
    },
    //

    [product],
  );

  const renderRiskLevel = (level: number) => {
    const maxLevel = 5;
    return Array(maxLevel)
      .fill(0)
      .map((_, i) => {
        const inactiveLevel = `${cdnURL}assets/products/general/inactive-risk-level.png`;
        const activeLevel = `${cdnURL}assets/products/general/active-risk-level.png`;

        return (
          <>
            {i < level ? (
              <img
                key={i}
                className={`w-3.5 h-3.5  ${i == 0 ? 'mr-[0.225rem]' : 'mr-[0.175rem]'}`}
                src={activeLevel}
                alt="risk"
              />
            ) : (
              <img
                key={i}
                className="ml-0.5 w-4 h-4"
                src={inactiveLevel}
                alt="non risk"
              />
            )}
          </>
        );
      });
  };

  const displayedTabs = () => {
    const tabs = [];

    tabs.push({
      key: 'about',
      title: messages.about,
      content: () => (
        <>
          {/* MOBILE ABOUT CARD */}
          <Card className="bg-transparent basis-full order-1 rounded-xxl px-0 py-0 lg:p-0">
            {/* "Who is" section */}
            {productCopy?.about && (
              <div className="product-md-container py-4">
                <Markdown>{productCopy?.about}</Markdown>
              </div>
            )}

            {/* STATS */}
            <Card className="bg-transparent basis-full self-stretch flex flex-row justify-between order-2 gap-4 py-2 px-0 lg:py-2 lg:px-0 rounded-xxl flex-wrap">
              {productCards.map((item, i) => {
                if (item?.value === 'riskLevel') {
                  const level = Number(getProductCopySection(item.value));
                  const info = getProductCopySection(item.value + '_info');
                  return (
                    <>
                      {level && (
                        <StatsBox
                          className="px-5 flex-col w-[calc(50%-0.5rem)] lg:w-auto flex-grow"
                          key={item.label}>
                          <div className="mb-2 font-semibold text-white flex flex-row items-center">
                            {renderRiskLevel(level)}
                          </div>
                          {info && (
                            <InfoButton
                              title={item.label}
                              text={info}
                              showTitle
                              useDesktopModal
                            />
                          )}
                          {!info && (
                            <p className="text-grey text-body-text">
                              {item?.label}
                            </p>
                          )}
                        </StatsBox>
                      )}
                    </>
                  );
                } else {
                  const sectionCopy = getProductCopySection(item.value);
                  const info = getProductCopySection(item.value + '_info');
                  return (
                    <>
                      {sectionCopy && (
                        <StatsBox
                          className="px-5 flex-col w-[calc(50%-0.5rem)] lg:w-auto flex-grow"
                          key={item.label}>
                          <h6 className="mb-0.5 font-semibold text-white">
                            {sectionCopy}
                          </h6>

                          {info && (
                            <InfoButton
                              title={item.label}
                              text={info}
                              showTitle
                              useDesktopModal
                            />
                          )}
                          {!info && (
                            <p className="text-grey text-body-text">
                              {item?.label}
                            </p>
                          )}
                        </StatsBox>
                      )}
                    </>
                  );
                }
              })}
            </Card>

            {productCopy?.aboutDisclaimer && (
              <div className="product-md-container mt-0 pt-0 lg:mt-4 py-4 px-2">
                <Markdown>{productCopy?.aboutDisclaimer}</Markdown>
              </div>
            )}

            <div className="mt-2 flex flex-col justify-between px-2">
              {/* Heading */}
              <div className="flex flex-row lg:justify-between mr-2 items-center text-body-text font-semibold">
                {productCopy?.graphicHeading && (
                  <h4 className="product-md-container mt-4">
                    <Markdown>{productCopy?.graphicHeading}</Markdown>
                  </h4>
                )}
              </div>
              {/* Sub-Heading */}
              {productCopy?.graphicSubheading && (
                <div className="product-md-container text-grey text-md">
                  <Markdown>{productCopy?.graphicSubheading}</Markdown>
                </div>
              )}

              {/* Graphic */}
              <div className="flex flex-col mt-4 lg:mt-8">
                <img
                  src={productMobileChart}
                  alt={`${code}-chart`}
                  className="w-full"
                />
              </div>
              {/* Footnote */}
              {productCopy?.graphicFootnote && (
                <div className="product-md-container mt-4 text-grey text-sm text-center">
                  <Markdown>{productCopy?.graphicFootnote}</Markdown>
                </div>
              )}
            </div>

            {/* Mobile Copy */}
            <Card className="bg-transparent basis-full self-stretch visible rounded-xxl mb-4 pt-0 lg:mt-4 py-4 px-2">
              <div className="block text-grey">
                {productTabs.map((item, i) => {
                  const sectionCopy = getProductCopySection(item.value);
                  return (
                    sectionCopy && (
                      <div key={item.label}>
                        <h4 className="mt-4 mb-2 font-semibold leading-9">
                          {item?.label}
                        </h4>
                        <div className="product-md-container mt-2 text-grey">
                          <Markdown
                            styles={{
                              link: {
                                color: '#626AFF',
                                fontWeight: 'bold',
                                textDecorationLine: 'none',
                                fontFamily: 'bozon',
                              },
                              text: { lineHeight: 25 },
                              bullet_list: { lineHeight: 22, color: '#a4a7c1' },
                            }}>
                            {sectionCopy}
                          </Markdown>
                        </div>
                      </div>
                    )
                  );
                })}
              </div>
            </Card>
          </Card>
        </>
      ),
    });

    // Show tab sections if holdings, vault balance or vault reserved amount > 0
    if (product?.totalAmount! > 0) {
      tabs.unshift({
        key: 'holdings',
        title: messages.yourHoldings,
        content: () => (
          <>
            <>
              <AssetCard
                showAssetDetails={true}
                iconComponent={CurrencyIcon}
                iconProps={{
                  code: product?.code!,
                  size: 24,
                  style: { marginRight: 5 },
                }}
                showButton
                assetName={product?.name}
                assetSubtitle={moment(
                  product?.holdingsLastChangedAt + 'Z',
                ).format('DD MMMM YYYY · HH:mm')}
                items={holdingsItems}
                onButtonPress={() => window.open(productCopy?.redeemLink ?? '')}
                buttonText={messages.redeemButton}
                className={'w-full my-4 gap-2 text-base'}
              />
            </>
          </>
        ),
      });
    }

    return tabs;
  };

  return (
    <>
      <div className="flex-col flex lg:flex-row gap-4 justify-between flex-wrap px-5 lg:px-6">
        {/* Performance Section */}
        <Card className="bg-neutral-600 basis-full lg:basis-[calc(50%-0.5rem)] rounded-xxl flex-shrink-0 order-2 hidden lg:block">
          <div className="mt-2 flex flex-col justify-between">
            {/* Heading */}
            <div className="flex flex-row lg:justify-between mr-2 items-center text-body-text font-semibold">
              {productCopy?.graphicHeading && (
                <h4 className="product-md-container mt-4">
                  <Markdown>{productCopy?.graphicHeading}</Markdown>
                </h4>
              )}
            </div>
            {/* Sub-Heading */}
            {productCopy?.graphicSubheading && (
              <div className="product-md-container text-grey text-md">
                <Markdown>{productCopy?.graphicSubheading}</Markdown>
              </div>
            )}

            {/* Graphic */}
            <div className="flex flex-col mt-4 lg:mt-8">
              <img
                src={productChart}
                alt={`${code}-chart`}
                className="w-full"
              />
            </div>
            {/* Footnote */}
            {productCopy?.graphicFootnote && (
              <div className="product-md-container mt-4 text-grey text-sm text-center">
                <Markdown>{productCopy?.graphicFootnote}</Markdown>
              </div>
            )}
          </div>
          {/* End of Performance section */}
        </Card>

        {/* HOLDINGS */}
        {product?.totalAmount && product?.totalAmount > 0 ? (
          <Card className="bg-transparent lg:px-0 lg:py-3 basis-full self-stretch order-3 lg:order-last hidden lg:block rounded-xxl">
            {/* Holdings section for crypto and savings */}
            {isVerified && (
              <>
                {/* Product name/ page title */}
                <div className="">
                  {/* Holdings headers/tabs */}
                  <h4 className="mt-0 mb-4">{messages.yourHoldings}</h4>
                  {/* Holdings information/values */}
                  <AdvancedTable
                    data={holdingsRows}
                    columns={columns}
                    headerColor="bg-neutral-500"
                    padContent={false}
                  />
                </div>
              </>
            )}
          </Card>
        ) : null}

        {/* STATS */}
        <Card className="bg-transparent basis-full self-stretch flex flex-row justify-between order-2 gap-4 py-2 px-0 lg:py-2 lg:px-0 rounded-xxl flex-wrap hidden lg:flex">
          {productCards.map((item, i) => {
            if (item?.value === 'riskLevel') {
              const level = Number(getProductCopySection(item.value));
              const info = getProductCopySection(item.value + '_info');
              return (
                <>
                  {level && (
                    <StatsBox
                      className="px-5 flex-col w-[calc(50%-0.5rem)] lg:w-auto flex-grow"
                      key={item.label}>
                      <div className="mb-2 font-semibold text-white flex flex-row items-center">
                        {renderRiskLevel(level)}
                      </div>
                      {info && (
                        <InfoButton
                          title={item.label}
                          text={info}
                          showTitle
                          useDesktopModal
                        />
                      )}
                      {!info && (
                        <p className="text-grey text-body-text">
                          {item?.label}
                        </p>
                      )}
                    </StatsBox>
                  )}
                </>
              );
            } else {
              const sectionCopy = getProductCopySection(item.value);
              const info = getProductCopySection(item.value + '_info');
              return (
                <>
                  {sectionCopy && (
                    <StatsBox
                      className="px-5 flex-col w-[calc(50%-0.5rem)] lg:w-auto flex-grow"
                      key={item.label}>
                      <h6 className="mb-0.5 font-semibold text-white">
                        {sectionCopy}
                      </h6>

                      {info && (
                        <InfoButton
                          title={item.label}
                          text={info}
                          showTitle
                          useDesktopModal
                        />
                      )}
                      {!info && (
                        <p className="text-grey text-body-text">
                          {item?.label}
                        </p>
                      )}
                    </StatsBox>
                  )}
                </>
              );
            }
          })}
        </Card>

        {/* DESKTOP ABOUT CARD */}
        <Card className="bg-neutral-600 basis-[calc(50%-0.5rem)] flex-shrink-0 order-3 lg:order-1 rounded-xxl px-0 lg:px-0 py-0 lg:py-0 hidden lg:block">
          <div
            className="flex flex-col justify-start p-4 lg:p-8 h-[15rem] rounded-tl-xxl rounded-tr-xxl"
            style={{
              background: `no-repeat top right url(${productBanner})`,
              backgroundSize: 'cover',
            }}>
            <div className="h-24 w-24">
              <CurrencyIcon
                currencyCode={code?.toUpperCase()}
                className={'h-14 w-14'}
              />
            </div>
            <h6 className="mb-4 font-semibold leading-9">
              {getCurrencyTypeLabel(
                code,
                product?.type ?? ActiveProductDtoTypeEnum.PrivateCredit,
                language,
              )}
            </h6>
            <h2 className="mb-4 font-semibold leading-9">{product?.name}</h2>
            <h6 className="mb-4 font-semibold leading-9">{code}</h6>
          </div>
          {/* "Who is" section */}

          {productCopy?.about && (
            <div className="product-md-container p-4 lg:p-8 text-neutral-400">
              <Heading level={4} className="mb-2 flex">
                {messages?.about}
              </Heading>
              <Markdown>{productCopy?.about}</Markdown>
            </div>
          )}

          {/* Product page buttons */}
          <div className="fixed bottom-0 left-0 z-30 w-full lg:static lg:mt-2 lg:p-0">
            {product && (
              <ProductButtons
                product={product}
                isVerified={isVerified}
                earlyAccessLink={productCopy?.earlyAccessLink}
                earlyAccessText={productCopy?.earlyAccessLinkText}
                redeemLink={productCopy?.redeemLink}
              />
            )}

            <div className="wrap flex justify-between mx-6 px-2">
              {!isVerified && !isVerificationPending && (
                <div className="fixed bottom-0 left-0 z-40 flex w-full items-center justify-center px-3 pb-4 lg:static lg:mr-2 lg:w-1/3 lg:pb-0">
                  <Button
                    type="button"
                    main
                    full
                    onClick={() => navigate({ to: '/verification' })}>
                    {messages.notVerified}
                  </Button>
                </div>
              )}
              {isVerificationPending ||
                (isVerificationInProgress && (
                  <div className="fixed bottom-0 left-0 z-40 flex w-full items-center justify-center px-3 pb-4 lg:static lg:mr-2 lg:w-1/3 lg:pb-0">
                    <Button
                      type="button"
                      main
                      full
                      isVerificationPending
                      onClick={() => setVerifyModal(true)}>
                      {messages.verifyPending}
                    </Button>
                  </div>
                ))}
            </div>
          </div>

          {productCopy?.aboutDisclaimer && (
            <div className="product-md-container mt-4 p-4 lg:p-8">
              <Markdown>{productCopy?.aboutDisclaimer}</Markdown>
            </div>
          )}
        </Card>

        {/* DESKTOP COPY AND TABS */}
        <Card className="bg-neutral-600 basis-full self-stretch order-2 lg:order-last hidden lg:block py-2 lg:py-2 rounded-xxl p-4 lg:p-8 mb-4">
          <div className="px-0">
            <ProductTabs
              items={productTabs}
              activeIndex={activeTabIndex}
              onClickItem={(tab: ProductTabItem) => handleTabSelect(tab.value)}
            />
          </div>
          <div className="hidden md:block text-grey">
            {productTabCopy && (
              <div className="product-md-container mt-4 text-grey">
                <Markdown
                  styles={{
                    link: {
                      color: '#626AFF',
                      fontWeight: 'bold',
                      textDecorationLine: 'none',
                      fontFamily: 'bozon',
                    },
                    bullet_list: { lineHeight: 22, color: '#a4a7c1' },
                  }}>
                  {productTabCopy}
                </Markdown>
              </div>
            )}
          </div>
        </Card>

        {/* MOBILE VIEW */}
        <Card className="bg-transparent basis-full self-stretch visible lg:hidden rounded-xxl mb-4 pt-0 lg:mt-4 py-4 px-2">
          <Card className="bg-transparent basis-full rounded-xxl px-0 lg:px-0 py-0 lg:py-0">
            <div
              className="flex flex-col justify-start items-center p-4 pt-20 lg:p-8 h-[22rem] lg:h-[15rem] rounded-tl-xxl rounded-tr-xxl"
              style={{
                background: `no-repeat top right url(${productMobileBanner})`,
                backgroundSize: 'cover',
              }}>
              <div className="h-16 w-16">
                <CurrencyIcon
                  currencyCode={code?.toUpperCase()}
                  className={'h-14 w-14'}
                />
              </div>
              <h6 className="mb-1 font-semibold leading-9">
                {getCurrencyTypeLabel(
                  code,
                  product?.type ?? ActiveProductDtoTypeEnum.PrivateCredit,
                  language,
                )}
              </h6>
              <h2 className="mb-1 font-semibold leading-9">{product?.name}</h2>
              <h6 className="mb-1 font-semibold leading-9">{code}</h6>
            </div>
          </Card>
          <CustomTabs floatingTabs={true} listOfElements={displayedTabs()} />
          {/* Product page buttons */}
          <div className="fixed bottom-0 left-0 z-30 w-full p-4 lg:static lg:mt-8 lg:p-0">
            {product && (
              <ProductButtons
                product={product}
                isVerified={isVerified}
                earlyAccessLink={productCopy?.earlyAccessLink}
                earlyAccessText={productCopy?.earlyAccessLinkText}
              />
            )}
            <div className="mt-8 flex items-center justify-start">
              {/* JK - Adding a conditional check here in order to re-use the div below (and
                        its elaborate Tailwind classes) for various button states when the user does
                        not have holdings */}
              {!isVerified && !isVerificationPending && (
                <div className="fixed bottom-0 left-0 z-40 flex w-full items-center justify-center px-3 pb-4 lg:static lg:mr-8 lg:w-1/3 lg:pb-0">
                  <Button
                    type="button"
                    main
                    full
                    onClick={() => navigate({ to: '/verification' })}>
                    {messages.notVerified}
                  </Button>
                </div>
              )}
              {!isVerified && !isVerificationPending && (
                <div className="fixed bottom-0 left-0 z-40 flex w-full items-center justify-center px-3 pb-4 lg:static lg:mr-8 lg:w-1/3 lg:pb-0">
                  <Button
                    type="button"
                    main
                    full
                    onClick={() => navigate({ to: '/verification' })}>
                    {messages.notVerified}
                  </Button>
                </div>
              )}
              {isVerificationPending && (
                <div className="fixed bottom-0 left-0 z-40 flex w-full items-center justify-center px-3 pb-4 lg:static lg:mr-8 lg:w-1/3 lg:pb-0">
                  <Button
                    type="button"
                    main
                    full
                    isVerificationPending
                    onClick={() => setVerifyModal(true)}>
                    {messages.verifyPending}
                  </Button>
                </div>
              )}
              {isVerificationInProgress && (
                <div className="fixed bottom-0 left-0 z-40 flex w-full items-center justify-center px-3 pb-4 lg:static lg:mr-8 lg:w-1/3 lg:pb-0">
                  <Button
                    type="button"
                    main
                    full
                    onClick={() => setVerifyModal(true)}>
                    {messages.verifyPending}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* wired */}
      <Modals.ReturnsModal
        messages={messages}
        isOpen={returnsModalOpen}
        hide={() => setReturnsModalOpen(false)}
      />
      <Modals.VerifyModal
        verificationPendingMessages={verificationPendingMessages}
        sub
        messages={messages}
        hide={() => setVerifyModal(false)}
        isOpen={verifyModal}
      />
    </>
  );
}

export default PrivateCreditProductData;
